import React, { useEffect, useRef, type ReactElement } from "react";
import { pickLoopAnimationName, spineFactionsPrefix } from "./spineFactionUtils";
import { preventContextMenuOnDesktop } from "../landing/landingDomGuards";

type SpineCanvasClass = import("@esotericsoftware/spine-webgl").SpineCanvas;

/** Encode each path segment for fetch URLs (e.g. spaces in `Dark Wanderer.json` break on mobile Safari). */
function spineAssetPath(name: string): string {
  return name.split("/").map((segment) => encodeURIComponent(segment)).join("/");
}

export type FactionSpineCanvasProps = {
  active: boolean;
  jsonFile: string;
  atlasFile: string;
  onLoadFailed: () => void;
  /** Fires once after the first frame that draws the skeleton (WebGL is ready). */
  onReady?: () => void;
};

/**
 * Spine WebGL preview for one skeleton. Only mounts while `active` is true
 * (saves GPU when off-screen). Calls `onLoadFailed` if assets or WebGL init fail.
 */
const FactionSpineCanvas = ({
  active,
  jsonFile,
  atlasFile,
  onLoadFailed,
  onReady,
}: FactionSpineCanvasProps): ReactElement => {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const spineRef = useRef<SpineCanvasClass | null>(null);
  /** Parent often passes inline handlers; if they were effect deps, any re-render would dispose WebGL (visible as flicker during scroll / IO updates). */
  const onLoadFailedRef = useRef(onLoadFailed);
  const onReadyRef = useRef(onReady);
  onLoadFailedRef.current = onLoadFailed;
  onReadyRef.current = onReady;

  useEffect(() => {
    if (!active) {
      spineRef.current?.dispose();
      spineRef.current = null;
      return;
    }

    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    let cancelled = false;
    let started = false;

    const layoutCanvas = () => {
      const rect = wrap.getBoundingClientRect();
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    };

    const startSpine = async () => {
      if (started || spineRef.current) return;
      try {
        layoutCanvas();
        if (wrap.clientWidth < 8 || wrap.clientHeight < 8) {
          requestAnimationFrame(() => {
            if (!cancelled && active) void startSpine();
          });
          return;
        }

        const [{ SpineCanvas, ResizeMode }, spineCore] = await Promise.all([
          import("@esotericsoftware/spine-webgl"),
          import("@esotericsoftware/spine-core"),
        ]);
        if (cancelled) return;
        started = true;

        const {
          AtlasAttachmentLoader,
          Skeleton,
          SkeletonJson,
          AnimationState,
          AnimationStateData,
        } = spineCore;

        const pathPrefix = spineFactionsPrefix();
        const jsonPath = spineAssetPath(jsonFile);
        const atlasPath = spineAssetPath(atlasFile);

        let skeleton: InstanceType<typeof Skeleton> | null = null;
        let animState: InstanceType<typeof AnimationState> | null = null;
        let firstReadyNotified = false;
        /** Some atlases (e.g. Naaz) still flash if we signal after a single draw; wait a few frames. */
        let completedDrawFrames = 0;

        const spineCanvas = new SpineCanvas(canvas, {
          pathPrefix,
          app: {
            loadAssets(c) {
              c.assetManager.loadJson(jsonPath);
              c.assetManager.loadTextureAtlas(atlasPath);
            },
            initialize(c) {
              try {
                const atlas = c.assetManager.require(atlasPath);
                const jsonData = c.assetManager.require(jsonPath);
                const skelJson = new SkeletonJson(new AtlasAttachmentLoader(atlas));
                const skeletonData = skelJson.readSkeletonData(jsonData);
                Skeleton.yDown = false;
                skeleton = new Skeleton(skeletonData);
                animState = new AnimationState(new AnimationStateData(skeletonData));
                const animName = pickLoopAnimationName(skeletonData);
                animState.setAnimation(0, animName, true);
                skeleton.setToSetupPose();
                skeleton.updateWorldTransform();
              } catch {
                if (!cancelled) onLoadFailedRef.current();
              }
            },
            update(_c, delta) {
              if (!skeleton || !animState) return;
              animState.update(delta);
              animState.apply(skeleton);
              skeleton.updateWorldTransform();
            },
            render(c) {
              if (!skeleton) return;
              c.renderer.resize(ResizeMode.Expand);
              const w = c.htmlCanvas.width;
              const h = c.htmlCanvas.height;
              c.renderer.camera.position.set(w / 2, h / 2, 0);

              const d = skeleton.data;
              const boundsW = d.width || 650;
              const boundsH = d.height || 1280;
              const land = c.htmlCanvas.closest(".land-home");
              let margin = 0.99;
              if (land instanceof HTMLElement) {
                const raw = getComputedStyle(land)
                  .getPropertyValue("--world-spine-fit-margin")
                  .trim();
                const parsed = parseFloat(raw);
                if (!Number.isNaN(parsed) && parsed >= 0.5 && parsed <= 1) {
                  margin = parsed;
                }
              }
              const fit =
                Math.min((w / boundsW) * margin, (h / boundsH) * margin) ||
                0.0001;

              skeleton.scaleX = fit;
              skeleton.scaleY = fit;
              const cx = d.x + boundsW * 0.5;
              const cy = d.y + boundsH * 0.5;
              skeleton.x = w * 0.5 - cx * fit;
              // Center JSON bounds in the canvas (contain already fits full AABB; avoids top clip when box is short).
              skeleton.y = h * 0.5 - cy * fit;

              c.clear(0, 0, 0, 0);
              c.renderer.begin();
              c.renderer.drawSkeleton(skeleton, false);
              c.renderer.end();

              // After first draw + a couple more frames so GPU textures settle (Naaz / cross-faction cases).
              if (!firstReadyNotified && !cancelled) {
                completedDrawFrames += 1;
                if (completedDrawFrames >= 3) {
                  firstReadyNotified = true;
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      requestAnimationFrame(() => {
                        if (!cancelled) onReadyRef.current?.();
                      });
                    });
                  });
                }
              }
            },
            error(_c, errors) {
              if (!cancelled && Object.keys(errors).length > 0)
                onLoadFailedRef.current();
            },
            dispose() {
              skeleton = null;
              animState = null;
            },
          },
        });

        spineRef.current = spineCanvas;
      } catch {
        if (!cancelled) onLoadFailedRef.current();
      }
    };

    void startSpine();

    const ro = new ResizeObserver(() => {
      layoutCanvas();
    });
    ro.observe(wrap);

    return () => {
      cancelled = true;
      ro.disconnect();
      spineRef.current?.dispose();
      spineRef.current = null;
    };
  }, [active, jsonFile, atlasFile]);

  return (
    <div ref={wrapRef} className="faction-spine-wrap">
      <canvas
        ref={canvasRef}
        className="faction-spine-canvas"
        onContextMenu={preventContextMenuOnDesktop}
      />
    </div>
  );
};

export default FactionSpineCanvas;
